/**
 * The browser's copy of the appointment zone-name map.
 *
 * 🔴 THIS FILE IS A MIRROR. Its authority is
 * `src/PhoenixCapital.Infrastructure/Services/Appointments/AppointmentDisplayTime.cs`,
 * whose `ZoneDisplayNames` dictionary is what actually labels an outbound
 * invitation. Nothing imports across the language boundary, so the two are held
 * together by two tests rather than by discipline:
 *
 *   - `appointmentZoneParity.test.ts` (this side) reads the C# file.
 *   - `AppointmentZoneMirrorParityTests.cs` (that side) reads THIS file.
 *
 * Both directions are needed because CI is path-filtered: `backend` fires on
 * `src/**​/*.cs` and `frontend` on `src/PhoenixCapital.Web/**`, so a C#-only edit
 * never runs the TypeScript suite and a TypeScript-only edit never runs the .NET
 * one. A single parity test would be skipped by exactly the change it exists to
 * catch.
 *
 * ⚠️ The names are HAND-MAINTAINED on both sides, deliberately.
 * `Intl.DateTimeFormat`'s `timeZoneName` is ICU- and browser-dependent, which is
 * why the server rejected `TimeZoneInfo.StandardName` for the same job. Only the
 * OFFSET is computed from zone data — see `zoneOffsetLabel` — because an offset
 * is a number the platform cannot render two ways.
 */

export interface AppointmentZone {
  /** The IANA id. The stored value of `AppointmentAttendee.timeZone`. */
  readonly ianaId: string;
  /** Winter label, e.g. `Eastern Standard Time`. */
  readonly standardName: string;
  /** Summer label. Identical to `standardName` for a zone that observes no DST. */
  readonly daylightName: string;
  /**
   * The SEASON-NEUTRAL label — `Eastern Time`.
   *
   * Two jobs, and they are the same job. It is what the external-attendee zone
   * picker offers, so a staffer can find "Eastern Time" for an attendee in
   * Michigan without knowing that Farmington Hills is `America/Detroit`. And it
   * is what an in-app caption uses when it cannot name a season — a surface
   * spanning arbitrary dates has no single answer to "standard or daylight?".
   *
   * It lives HERE, beside the names it belongs to, rather than in the picker,
   * precisely so it cannot become a third hand-maintained list.
   */
  readonly pickerLabel: string;
}

/**
 * Mirrors `AppointmentDisplayTime.ZoneDisplayNames`, in the same order.
 *
 * 🔴 Both halves of a no-DST zone carry the SAME name on purpose. Arizona keeps
 * UTC−07:00 all year, so its daylight half is unreachable — which is exactly why
 * leaving "Mountain Daylight Time" there was a trap: nothing exercises it, so a
 * tzdata change or a refactor would surface MOUNTAIN DAYLIGHT TIME FOR ARIZONA
 * in the branch no test covers.
 */
export const APPOINTMENT_ZONES: readonly AppointmentZone[] = [
  { ianaId: "America/New_York", standardName: "Eastern Standard Time", daylightName: "Eastern Daylight Time", pickerLabel: "Eastern Time" },
  { ianaId: "America/Chicago", standardName: "Central Standard Time", daylightName: "Central Daylight Time", pickerLabel: "Central Time" },
  { ianaId: "America/Denver", standardName: "Mountain Standard Time", daylightName: "Mountain Daylight Time", pickerLabel: "Mountain Time" },
  { ianaId: "America/Phoenix", standardName: "Arizona Time", daylightName: "Arizona Time", pickerLabel: "Arizona Time" },
  { ianaId: "America/Los_Angeles", standardName: "Pacific Standard Time", daylightName: "Pacific Daylight Time", pickerLabel: "Pacific Time" },
  { ianaId: "America/Anchorage", standardName: "Alaska Standard Time", daylightName: "Alaska Daylight Time", pickerLabel: "Alaska Time" },
  { ianaId: "Pacific/Honolulu", standardName: "Hawaii-Aleutian Standard Time", daylightName: "Hawaii-Aleutian Standard Time", pickerLabel: "Hawaii Time" },
];

const BY_ID = new Map(APPOINTMENT_ZONES.map((z) => [z.ianaId, z]));

/** The mirror row for `tz`, or undefined when this module does not name it. */
export function appointmentZone(tz: string | null | undefined): AppointmentZone | undefined {
  return tz ? BY_ID.get(tz.trim()) : undefined;
}

/**
 * `GMT-07:00` for `tz` at `instant`.
 *
 * 🔴 The normalisation is not cosmetic. `longOffset` is specified loosely enough
 * that engines differ: Chrome and Node emit `GMT-07:00`, some emit `GMT-7`, and
 * every engine emits a BARE `GMT` for UTC. Left alone, a Phoenix caption would
 * read `Arizona Time (GMT-7)` in one browser and `Arizona Time (GMT-07:00)` in
 * the next while the email said the second — a difference a reader would
 * reasonably take for two different times.
 */
export function zoneOffsetLabel(tz: string | null | undefined, instant: Date): string {
  const zone = tz || "America/Phoenix";
  let raw = "";
  try {
    raw =
      new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longOffset" })
        .formatToParts(instant)
        .find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    // An unresolvable zone must not cost the caption — the surface is still
    // rendering real times, it just cannot name their zone.
    return "";
  }

  return normalizeOffsetLabel(raw);
}

/**
 * `GMT-7` → `GMT-07:00`, `GMT+5:45` → `GMT+05:45`, bare `GMT` → `GMT+00:00`.
 *
 * Exported so the shapes this exists to absorb can be tested directly: the
 * engine running the suite emits well-formed `GMT-07:00`, so driving this
 * through `Intl` alone would exercise only the case that never needed fixing.
 */
export function normalizeOffsetLabel(raw: string): string {
  // Bare "GMT" is UTC. Some engines emit it that way, and "(GMT)" beside a clock
  // reads as an abbreviation rather than as an offset of zero.
  if (raw === "GMT" || raw === "UTC") return "GMT+00:00";

  const m = /^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/.exec(raw);
  if (!m) return raw;

  const [, sign, hours, minutes] = m;
  return `GMT${sign}${hours.padStart(2, "0")}:${minutes ?? "00"}`;
}

/** True when `tz`'s UTC offset differs at the two instants. */
export function crossesTransition(
  tz: string | null | undefined,
  from: Date,
  to: Date,
): boolean {
  return zoneOffsetLabel(tz, from) !== zoneOffsetLabel(tz, to);
}

/**
 * The one-per-surface caption naming the zone every time on screen is rendered
 * in — the viewer's own profile zone, which is what these surfaces already use.
 *
 * @param anchor The surface's own date: the day a drawer is showing, the first
 *   day of a visible month. Pass `null` for a surface that spans arbitrary dates
 *   (a rolling list, an upcoming-items widget).
 * @param anchorEnd The last date in view, when the surface shows a range.
 *
 * 🔴 THE OFFSET IS DROPPED WHENEVER IT CANNOT BE TRUE OF EVERY ROW. A month grid
 * for March or November contains a DST transition, so ONE offset printed over it
 * is a false statement about half the cells beneath it; a rolling list has no
 * single date to compute one from at all. In both cases the caption falls back
 * to the season-neutral name, which stays true across any range. This is the one
 * place a per-row suffix would have been more precise than a caption, and it is
 * the trade the caption is deliberately making.
 */
export function zoneCaption(
  tz: string | null | undefined,
  anchor: Date | null,
  anchorEnd?: Date | null,
): string {
  const zone = appointmentZone(tz);

  if (anchor === null) {
    return zone
      ? `All times shown in ${zone.pickerLabel}`
      : captionFromOffset(tz, new Date());
  }

  const end = anchorEnd ?? anchor;
  if (zone && crossesTransition(tz, anchor, end)) {
    return `All times shown in ${zone.pickerLabel}`;
  }

  if (!zone) return captionFromOffset(tz, anchor);

  const isDaylight = zone.standardName !== zone.daylightName && inDaylightTime(tz, anchor);
  const name = isDaylight ? zone.daylightName : zone.standardName;
  const offset = zoneOffsetLabel(tz, anchor);
  return offset ? `All times shown in ${name} (${offset})` : `All times shown in ${name}`;
}

function captionFromOffset(tz: string | null | undefined, instant: Date): string {
  const offset = zoneOffsetLabel(tz, instant);
  // No name to print and no offset either: say nothing rather than something
  // shaped like a label.
  return offset ? `All times shown at ${offset}` : "";
}

/**
 * Whether `tz` is on its summer offset at `instant`, decided by comparing
 * against its own January and July offsets rather than by reading a name.
 * Hemisphere-agnostic: whichever of the two is further from UTC is the
 * daylight one.
 */
function inDaylightTime(tz: string | null | undefined, instant: Date): boolean {
  const year = instant.getUTCFullYear();
  const jan = zoneOffsetMinutes(tz, new Date(Date.UTC(year, 0, 15)));
  const jul = zoneOffsetMinutes(tz, new Date(Date.UTC(year, 6, 15)));
  if (jan === null || jul === null || jan === jul) return false;

  const now = zoneOffsetMinutes(tz, instant);
  return now !== null && now === Math.max(jan, jul);
}

function zoneOffsetMinutes(tz: string | null | undefined, instant: Date): number | null {
  const label = zoneOffsetLabel(tz, instant);
  const m = /^GMT([+-])(\d{2}):(\d{2})$/.exec(label);
  if (!m) return null;
  const [, sign, hours, minutes] = m;
  const total = Number(hours) * 60 + Number(minutes);
  return sign === "-" ? -total : total;
}
